import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { oneLineSummary } from "../util/parseDescription";
import Thumbnail from "./Thumbnail";
// Compose a row's stable id from (customer, bID) so the listbox's
// aria-activedescendant resolves to the right node even when two customers
// share a numeric bID (bIDs are per-shard).
function rowId(ev) {
    return `event-row-${ev.customer}-${ev.bID}`;
}
// Compact vertical list for StudioView's right column. Keyboard-friendly
// per D11: arrow keys move focus and Enter activates. role=listbox/option
// + aria-selected + aria-activedescendant give screen readers a complete
// model of which option is currently active.
export default function EventList({ events, selected, onSelect, emptyText }) {
    const containerRef = useRef(null);
    // Scroll the active row to the TOP of the visible scroll area when the
    // selection changes. `block: "start"` (vs "nearest") keeps the currently-
    // playing event pinned to a predictable position so the operator can
    // glance at the list without losing the active marker. Mirrors the
    // playlist UX in YouTube/Spotify.
    useEffect(() => {
        if (!selected)
            return;
        const node = containerRef.current?.querySelector(`#${CSS.escape(rowId(selected))}`);
        // jsdom doesn't implement scrollIntoView; guard so tests don't crash.
        if (node && typeof node.scrollIntoView === "function") {
            node.scrollIntoView({ block: "start", behavior: "smooth" });
        }
    }, [selected]);
    if (events.length === 0) {
        return (_jsx("div", { className: "event-list event-list-empty", children: _jsxs("div", { className: "empty-state", children: [_jsx("div", { className: "icon", "aria-hidden": "true", children: "\u2205" }), _jsx("div", { className: "copy", children: emptyText || "No matches. Try widening the date range." })] }) }));
    }
    const onKeyDown = (e) => {
        if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter")
            return;
        if (events.length === 0)
            return;
        // Compound match (customer, bID) — bIDs are per-shard; without the customer
        // gate, two same-bID events from different customers would collide on
        // selection lookup after a customer-switch race.
        const idx = selected
            ? events.findIndex(x => x.bID === selected.bID && x.customer === selected.customer)
            : -1;
        if (e.key === "Enter") {
            if (idx >= 0)
                onSelect(events[idx]);
            return;
        }
        e.preventDefault();
        const next = e.key === "ArrowDown"
            ? Math.min(idx + 1, events.length - 1)
            : Math.max(idx - 1, 0);
        if (next >= 0 && next !== idx)
            onSelect(events[next]);
    };
    // After a click, focus the listbox so subsequent arrow keys work without
    // a Tab. Without this, click-then-arrow does nothing until the user tabs
    // back into the container.
    const handleClick = (ev) => {
        onSelect(ev);
        containerRef.current?.focus();
    };
    const activeId = selected ? rowId(selected) : undefined;
    return (_jsx("div", { className: "event-list", role: "listbox", tabIndex: 0, onKeyDown: onKeyDown, ref: containerRef, "aria-label": "Events", "aria-activedescendant": activeId, children: events.map(ev => {
            const isActive = !!selected && selected.bID === ev.bID && selected.customer === ev.customer;
            const summary = oneLineSummary(ev.sDescription || "");
            const time = new Date(ev.tEventStart).toLocaleTimeString([], {
                hour: "2-digit", minute: "2-digit",
            });
            const date = new Date(ev.tEventStart).toLocaleDateString();
            return (_jsxs("div", { id: rowId(ev), role: "option", "aria-selected": isActive, className: isActive ? "event-row active" : "event-row", onClick: () => handleClick(ev), children: [_jsxs("div", { className: "event-thumb", children: [_jsx(Thumbnail, { ev: ev }), isActive && (_jsx("span", { className: "event-now-playing", "aria-hidden": "true", children: "\u25B6" }))] }), _jsxs("div", { className: "event-meta", children: [_jsxs("div", { className: "top", children: [_jsxs("span", { className: "time", children: [date, " ", time] }), _jsxs("span", { className: "cam", children: ["cam ", ev.bCamera] })] }), _jsx("div", { className: "summary", children: summary || _jsx("em", { className: "muted", children: "no description" }) })] })] }, `${ev.customer}:${ev.bID}`));
        }) }));
}
